import type { ParsedUrlQuery } from 'querystring';
import slug from 'slug';

/* Prefer slugify when generating urls from arbitrary text, eg category titles- produces clean human-readable URLs.
 * Prefer encodeURIComponent for query param values - handles &, =, ?, # correctly to parse url.
 */
export const getSlug = (text: string | null | undefined): string => {
  if (!text) return '';
  return slug(text);
};

export const appendParamsToUrl = (baseUrl: string, query: ParsedUrlQuery) => {
  const allowedParams = ['source', 'campaignId', 'utm_source', 'utm_term', 'utm_medium', 'gclid'];

  const filteredQuery = Object.keys(query)
    .filter((key) => allowedParams.includes(key))
    .reduce((obj, key) => {
      obj[key] = query[key];
      return obj;
    }, {});

  const queryString = new URLSearchParams(filteredQuery).toString();
  const separator = baseUrl.includes('?') ? '&' : '?';
  const fullUrl = queryString ? `${baseUrl}${separator}${queryString}` : baseUrl;

  return fullUrl;
};

export function removeLeadingSlash(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path;
}

export function buildUrlRemovingParams(
  pathname: string,
  searchParams: URLSearchParams | Readonly<URLSearchParams> | string | null | undefined,
  keysToRemove: readonly string[]
): string {
  const base = typeof pathname === 'string' ? pathname : '';
  const params = new URLSearchParams(searchParams ?? '');

  let changed = false;
  keysToRemove.forEach((key) => {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  });

  if (!changed) return base;

  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export function mergeUrlParams(currentUrl: string, params: Record<string, unknown>): string {
  const url = new URL(currentUrl);

  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return;
    const str = String(value);
    if (!str.trim()) return;
    url.searchParams.set(key, str);
  });

  return url.toString();
}
